/**
 * Support — the ticket queue and the reply thread (ADR-047, task B3).
 *
 * Left: the queue, filtered by status and optionally to the operator's own
 * assignments. Right: the selected thread, with reply, assign-to-me and a
 * status control. A human sends every reply — the `triage` chip, when present,
 * is the AI's proposal (ADR-056), never an action; the reply box stays empty
 * until an operator types.
 *
 * No money moves here and none can — this screen touches `tickets`,
 * `ticket_messages` and nothing else.
 */
import { useState, type ReactNode } from 'react';
import {
  fetchTicket,
  fetchTickets,
  patchTicket,
  replyTicket,
  type TicketDetail,
  type TicketStatus,
  type TicketSummary,
} from '../api/support.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age, shortTime } from '../lib/datetime.ts';
import { Badge, Card, EmptyState, ErrorState, TableSkeleton } from '../ui/primitives.tsx';
import { useToast } from '../ui/feedback.tsx';
import { IconInbound, IconPeople } from '../ui/icons.tsx';

const STATUSES: (TicketStatus | 'all')[] = ['open', 'pending', 'resolved', 'all'];

function tone(status: string): 'info' | 'pending' | 'win' | 'neutral' {
  return status === 'open' ? 'info' : status === 'pending' ? 'pending' : status === 'resolved' ? 'win' : 'neutral';
}

export function Support(): ReactNode {
  const [status, setStatus] = useState<TicketStatus | 'all'>('open');
  const [mine, setMine] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const queue = useAsync(
    () =>
      fetchTickets({
        ...(status === 'all' ? {} : { status }),
        ...(mine ? { assigned: 'me' as const } : {}),
      }),
    [status, mine],
  );
  const rows = queue.data ?? [];

  return (
    <div className="split">
      <div className="stack">
        <div className="toolbar">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={`tab${status === s ? ' is-active' : ''}`}
                onClick={() => setStatus(s)}
              >
                {s}
              </button>
            ))}
          </div>
          <span className="topbar__spacer" style={{ flex: 1 }} />
          <label className="field field--inline" style={{ margin: 0 }}>
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
            <span className="field__label" style={{ margin: 0 }}>Assigned to me</span>
          </label>
        </div>

        <Card title="Queue" icon={<IconInbound />} count={rows.length} flush>
          {queue.error && rows.length === 0 ? (
            <ErrorState title="The queue did not load" detail={queue.error} onRetry={queue.reload} />
          ) : queue.loading && rows.length === 0 ? (
            <TableSkeleton columns={['text', 'text', 'text', 'num']} rows={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<IconInbound size="lg" />}
              title="Nothing waiting"
              hint={mine ? 'No tickets are assigned to you in this view.' : 'No tickets match this filter. A clear queue is good news.'}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>#</th><th>Subject</th><th>Player</th><th>Status</th><th>Assignee</th><th className="right">Updated</th></tr>
                </thead>
                <tbody>
                  {rows.map((t: TicketSummary) => (
                    <tr
                      key={t.id}
                      className={`row--clickable${t.id === selected ? ' is-selected' : ''}`}
                      onClick={() => setSelected(t.id)}
                    >
                      <td className="mono dim">{t.id}</td>
                      <td className="cell-lead">
                        {t.subject}
                        {t.triage ? <Badge tone="info">triaged</Badge> : null}
                      </td>
                      <td className="muted nowrap">{t.username ?? `#${t.userId}`}</td>
                      <td><Badge tone={tone(t.status)}>{t.status}</Badge></td>
                      <td className="muted nowrap">{t.assignedOperatorUsername ?? '—'}</td>
                      <td className="right mono muted" title={absolute(t.updatedAt)}>{age(t.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Thread ticketId={selected} onChanged={queue.reload} />
    </div>
  );
}

function Thread({ ticketId, onChanged }: { ticketId: number | null; onChanged: () => void }): ReactNode {
  const detail = useAsync(
    () => (ticketId ? fetchTicket(ticketId) : Promise.resolve(null)),
    [ticketId],
  );
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  if (!ticketId) {
    return (
      <Card title="Thread" icon={<IconInbound />}>
        <EmptyState compact icon={<IconInbound size="lg" />} title="No ticket selected" hint="Pick a ticket to read and reply to the thread." />
      </Card>
    );
  }

  const t = detail.data;

  async function act(fn: () => Promise<TicketDetail>, done: string): Promise<void> {
    setBusy(true);
    try {
      await fn();
      toast(done);
      detail.reload();
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'That action failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function sendReply(): Promise<void> {
    if (!t || draft.trim() === '') return;
    await act(() => replyTicket(t.id, draft.trim()), 'Reply sent.');
    setDraft('');
  }

  return (
    <div className="stack">
      {detail.error ? (
        <Card title="Thread" icon={<IconInbound />}>
          <ErrorState title="The thread did not load" detail={detail.error} onRetry={detail.reload} />
        </Card>
      ) : !t ? (
        <Card title="Thread" icon={<IconInbound />}><TableSkeleton columns={['text', 'text']} rows={4} /></Card>
      ) : (
        <>
          <Card
            title={t.subject}
            sub={`#${t.id} · ${t.username ?? `player #${t.userId}`}`}
            icon={<IconPeople />}
            actions={<Badge tone={tone(t.status)}>{t.status}</Badge>}
          >
            <div className="kv kv--spaced">
              <span className="kv__k">Assignee</span>
              <span className="kv__v">{t.assignedOperatorUsername ?? '— unassigned'}</span>
              <span className="kv__k">Priority</span>
              <span className="kv__v">{t.priority}</span>
              <span className="kv__k">Opened</span>
              <span className="kv__v" title={absolute(t.createdAt)}>{shortTime(t.createdAt)}</span>
              {t.triage && t.triage.disposition !== 'draft' ? (
                <>
                  <span className="kv__k">AI triage</span>
                  <span className="kv__v mono">{JSON.stringify(t.triage).slice(0, 120)}</span>
                </>
              ) : null}
            </div>
            {t.triage?.disposition === 'draft' ? (
              <div className="subcard">
                <p className="field__hint">
                  <Badge tone="info">AI draft — not sent</Badge>{' '}
                  {t.triage.category ? `${t.triage.category} · ` : ''}
                  {t.triage.priority ? `suggested priority ${t.triage.priority} · ` : ''}
                  a human sends every reply.
                </p>
                {t.triage.draft ? (
                  <>
                    <p>{t.triage.draft}</p>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      disabled={busy || t.status === 'resolved'}
                      onClick={() => setDraft(t.triage?.draft ?? '')}
                      title="Copies the draft into the reply box — nothing is sent until you send it"
                    >
                      Use draft in reply box
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
            <div className="row-actions row-actions--start">
              <button type="button" className="btn btn--sm btn--ghost" disabled={busy}
                onClick={() => void act(() => patchTicket(t.id, { assignedOperatorId: 'me' }), 'Assigned to you.')}>
                Assign to me
              </button>
              {t.status !== 'resolved' ? (
                <button type="button" className="btn btn--sm btn--primary" disabled={busy}
                  onClick={() => void act(() => patchTicket(t.id, { status: 'resolved' }), 'Ticket resolved.')}>
                  Resolve
                </button>
              ) : (
                <button type="button" className="btn btn--sm btn--ghost" disabled={busy}
                  onClick={() => void act(() => patchTicket(t.id, { status: 'open' }), 'Ticket reopened.')}>
                  Reopen
                </button>
              )}
            </div>
          </Card>

          <Card title="Conversation" icon={<IconInbound />} count={t.messages.length} flush>
            <div className="card__body">
              <div className="thread">
                {t.messages.map((m) => (
                  <div key={m.id} className={`msg msg--${m.authorType}`}>
                    <div className="msg__head">
                      <span className="msg__author">
                        {m.authorType === 'operator' ? (m.operatorUsername ?? 'operator') : m.authorType}
                      </span>
                      <span title={absolute(m.createdAt)}>{shortTime(m.createdAt)}</span>
                    </div>
                    <div className="msg__body">{m.body}</div>
                  </div>
                ))}
              </div>

              {t.status === 'resolved' ? (
                <p className="field__hint" style={{ marginTop: 'var(--space-3)' }}>
                  This ticket is resolved. Reopen it to reply.
                </p>
              ) : (
                <form className="field" style={{ marginTop: 'var(--space-4)' }}
                  onSubmit={(e) => { e.preventDefault(); void sendReply(); }}>
                  <span className="field__label">Reply</span>
                  <textarea className="input" rows={3} value={draft}
                    placeholder="A human sends every reply."
                    onChange={(e) => setDraft(e.target.value)} />
                  <div className="row-actions row-actions--start">
                    <button type="submit" className="btn btn--sm btn--primary" disabled={busy || draft.trim() === ''}>
                      Send reply
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
